// ============================================================================
// Componente: FiscalVerificacaoAliquotas.js
// Objetivo: Auditar as alíquotas de PIS/COFINS das vendas das 4 empresas
//           (Hunger, P&G, P&M, GC Food) gravadas na dtbSovosItens.
//
// Duas conferências (feitas no backend — GET /api/fiscal/verificacao-aliquotas):
//   1) Alíquota (só CST 01): tem de bater com o regime da empresa.
//      Lucro Real → 1,65 / 7,60; Lucro Presumido → 0,65 / 3,00.
//      A HUNGER virou Lucro Real em 01/01/2026 (antes, Presumido) — o esperado
//      depende da data de emissão.
//   2) Base no CST 06 (alíquota zero): o normal é base 0,00; a anomalia é a linha
//      CST 06 com base de cálculo > 0.
//
// A tela mostra SÓ as divergências. Renderizada DENTRO da aba de Auditoria Fiscal
// (embedded), então não desenha casco próprio.
// ============================================================================

import React, { useState, useCallback } from 'react';
import { Search, Download, AlertTriangle, ShieldCheck, Info } from 'lucide-react';

const COR = '#7c3aed';        // roxo do Fiscal
const COR_ESCURA = '#6d28d9';
const ITENS_POR_PAGINA = 50;

const EMPRESAS = [
    { value: 'Todas', label: 'Todas as empresas' },
    { value: 'Hunger', label: 'Hunger' },
    { value: 'PG', label: 'P&G' },
    { value: 'PM', label: 'P&M' },
    { value: 'GC', label: 'GC Food' },
];

// Colunas exibidas (ordem fixa). num = numérica (alinhada à direita); center = centralizada.
const COLUNAS = [
    { key: 'DT_EMISSAO', label: 'Emissão' },
    { key: 'EMPRESA', label: 'Empresa' },
    { key: 'REGIME', label: 'Regime' },
    { key: 'VERIFICACAO', label: 'Verificação' },
    { key: 'NR_CNPJ_CPF_EMITENTE', label: 'CNPJ Emitente' },
    { key: 'CD_PRODUTO', label: 'Cód. Produto' },
    { key: 'DS_PRODUTO', label: 'Produto' },
    { key: 'CD_CST_PIS', label: 'CST PIS', center: true },
    { key: 'CD_CST_COFINS', label: 'CST COFINS', center: true },
    { key: 'VL_TOTAL', label: 'Vl. Total', num: true },
    { key: 'VL_DESCONTO', label: 'Vl. Desconto', num: true },
    { key: 'VL_ICMS', label: 'Vl. ICMS', num: true },
    { key: 'VL_BASE_CALCULO_PIS', label: 'Base PIS', num: true },
    { key: 'VL_BASE_CALCULO_COFINS', label: 'Base COFINS', num: true },
    { key: 'BASE_CORRETA', label: 'Base correta', num: true },
    { key: 'VL_PIS', label: 'PIS', num: true },
    { key: 'VL_PIS_CORRETO', label: 'PIS correto', num: true },
    { key: 'VL_COFINS', label: 'COFINS', num: true },
    { key: 'VL_COFINS_CORRETO', label: 'COFINS correto', num: true },
    { key: 'SOMA_CORRETA', label: 'Soma correta (PIS+COFINS)', num: true },
    { key: 'NR_CHAVE_ACESSO', label: 'Chave de Acesso' },
    { key: 'MOTIVO', label: 'Motivo da divergência' },
];

// Formata como moeda BR (R$).
const fmtBRL = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtNum = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? (v ?? '') : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Datas padrão: do 1º dia do mês corrente até hoje (período curto = consulta leve).
const isoHoje = () => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
};
const isoInicioMes = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const FiscalVerificacaoAliquotas = () => {
    const [empresa, setEmpresa] = useState('Todas');
    const [dataInicio, setDataInicio] = useState(isoInicioMes());
    const [dataFim, setDataFim] = useState(isoHoje());

    const [divergencias, setDivergencias] = useState([]);
    const [totais, setTotais] = useState(null);
    const [truncado, setTruncado] = useState(false);
    const [limite, setLimite] = useState(0);
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState('');
    const [pesquisado, setPesquisado] = useState(false);
    const [paginaAtual, setPaginaAtual] = useState(1);

    const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

    const montarQuery = () => {
        const qs = new URLSearchParams();
        qs.set('dataInicio', dataInicio);
        qs.set('dataFim', dataFim);
        if (empresa) qs.set('empresa', empresa);
        return qs.toString();
    };

    const buscar = useCallback(async () => {
        if (!dataInicio || !dataFim) { setErro('Informe o período (data início e data fim).'); return; }
        if (dataFim < dataInicio) { setErro('A data final não pode ser anterior à inicial.'); return; }

        setLoading(true); setErro(''); setPesquisado(true); setPaginaAtual(1);
        try {
            const r = await fetch(`/api/fiscal/verificacao-aliquotas?${montarQuery()}`, { headers: auth() });
            if (!r.ok) {
                const txt = await r.text();
                throw new Error(`HTTP ${r.status} — ${txt}`);
            }
            const data = await r.json();
            setDivergencias(Array.isArray(data.divergencias) ? data.divergencias : []);
            setTotais(data.totais || null);
            setTruncado(!!data.truncado);
            setLimite(data.limite || 0);
        } catch (e) {
            setErro(e.message);
            setDivergencias([]);
            setTotais(null);
            setTruncado(false);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresa, dataInicio, dataFim]);

    const exportarXlsx = async () => {
        try {
            const r = await fetch(`/api/fiscal/verificacao-aliquotas?${montarQuery()}&formato=xlsx`, { headers: auth() });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Verificacao_Aliquotas_${dataInicio}_a_${dataFim}_${empresa}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErro('Erro ao exportar: ' + e.message);
        }
    };

    const totalPaginas = Math.max(1, Math.ceil(divergencias.length / ITENS_POR_PAGINA));
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    const pagina = divergencias.slice(inicio, inicio + ITENS_POR_PAGINA);

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <ShieldCheck size={24} color={COR} />
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Verificação de Alíquotas (PIS/COFINS)</h3>
            </div>

            {/* Explicação das regras */}
            <div style={{ background: '#f5f3ff', borderLeft: `4px solid ${COR}`, padding: '14px 18px', borderRadius: 8, marginBottom: 20 }}>
                <p style={{ margin: 0, color: '#4c1d95', fontSize: 13.5, lineHeight: 1.6 }}>
                    Confere as vendas das 4 empresas na base Sovos e devolve <strong>apenas as divergências</strong>, em duas verificações:
                    <br />• <strong>CST 06</strong> (alíquota zero): a base de cálculo de PIS e COFINS tem de ser <strong>zero</strong> — sinalizamos a linha que tiver base maior que zero.
                    <br />• <strong>CST 01</strong> (tributado): a <strong>base correta</strong> é <em>Vl. Total − Desconto − ICMS</em>; e o <strong>imposto correto</strong> é base × alíquota do regime —
                    Lucro Real <strong>1,65% / 7,60%</strong>, Presumido <strong>0,65% / 3,00%</strong> (a <strong>Hunger</strong> é Lucro Real a partir de 01/01/2026). A comparação é exata — qualquer diferença é sinalizada.
                    <br />A tabela traz o <strong>PIS correto</strong>, o <strong>COFINS correto</strong> e a <strong>soma correta</strong> de cada linha.
                </p>
            </div>

            {/* Filtros */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr)) auto auto', gap: 12, alignItems: 'end' }}>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Empresa</label>
                        <select value={empresa} onChange={(e) => setEmpresa(e.target.value)}
                            style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }}>
                            {EMPRESAS.map((emp) => <option key={emp.value} value={emp.value}>{emp.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Data início (emissão)</label>
                        <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)}
                            style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Data fim (emissão)</label>
                        <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)}
                            style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                    </div>
                    <button onClick={buscar} disabled={loading} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '10px 16px', background: loading ? '#94a3b8' : COR_ESCURA, color: '#fff',
                        border: 'none', borderRadius: 8, fontWeight: 800, cursor: loading ? 'wait' : 'pointer',
                    }}>
                        <Search size={16} /> {loading ? 'Verificando…' : 'Verificar'}
                    </button>
                    <button onClick={exportarXlsx} disabled={loading || divergencias.length === 0} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '10px 16px', background: divergencias.length === 0 ? '#e2e8f0' : '#059669',
                        color: divergencias.length === 0 ? '#64748b' : '#fff',
                        border: 'none', borderRadius: 8, fontWeight: 800,
                        cursor: divergencias.length === 0 ? 'not-allowed' : 'pointer',
                    }}>
                        <Download size={16} /> Exportar XLSX
                    </button>
                </div>
                <p style={{ margin: '12px 0 0', fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Info size={13} /> A base Sovos é enorme — quanto menor o período, mais rápida a verificação.
                </p>
            </div>

            {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}

            {/* Aviso de resultado truncado */}
            {truncado && (
                <div style={{ padding: 12, background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AlertTriangle size={18} /> Foram encontradas mais de {limite.toLocaleString('pt-BR')} divergências — mostrando apenas as primeiras {limite.toLocaleString('pt-BR')}. Estreite o período ou filtre por empresa para ver o restante.
                </div>
            )}

            {/* Resumo */}
            {pesquisado && !loading && !erro && (
                divergencias.length === 0 ? (
                    <div style={{ padding: 16, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12, color: '#065f46', display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600 }}>
                        <ShieldCheck size={20} /> Nenhuma divergência encontrada no período. Alíquotas e bases corretas.
                    </div>
                ) : (
                    <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
                        <strong style={{ color: '#991b1b' }}>{divergencias.length.toLocaleString('pt-BR')}</strong> divergência(s) encontrada(s).
                        {divergencias.length > ITENS_POR_PAGINA && ` · Página ${paginaAtual} de ${totalPaginas}`}
                    </div>
                )
            )}

            {/* Total da soma correta (PIS+COFINS) das divergências */}
            {divergencias.length > 0 && totais && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: '1 1 200px', background: '#f5f3ff', border: `1px solid #ddd6fe`, borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ fontSize: 12, color: '#6d28d9', fontWeight: 700 }}>PIS correto (total)</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{fmtBRL(totais.pisCorreto)}</div>
                    </div>
                    <div style={{ flex: '1 1 200px', background: '#f5f3ff', border: `1px solid #ddd6fe`, borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ fontSize: 12, color: '#6d28d9', fontWeight: 700 }}>COFINS correto (total)</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{fmtBRL(totais.cofinsCorreto)}</div>
                    </div>
                    <div style={{ flex: '1 1 200px', background: COR, borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ fontSize: 12, color: '#ede9fe', fontWeight: 700 }}>Soma correta PIS+COFINS</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{fmtBRL(totais.somaCorreta)}</div>
                    </div>
                </div>
            )}

            {/* Tabela de divergências */}
            {divergencias.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead style={{ background: '#f5f3ff', position: 'sticky', top: 0 }}>
                            <tr>
                                {COLUNAS.map((c) => (
                                    <th key={c.key} style={{
                                        padding: 10, textAlign: c.num ? 'right' : (c.center ? 'center' : 'left'),
                                        fontWeight: 800, color: COR_ESCURA,
                                        borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
                                    }}>{c.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pagina.map((row, i) => (
                                <tr key={inicio + i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    {COLUNAS.map((c) => (
                                        <td key={c.key} style={{
                                            padding: 10,
                                            color: c.key === 'MOTIVO' ? '#991b1b' : '#0f172a',
                                            fontWeight: c.key === 'MOTIVO' ? 700 : 400,
                                            textAlign: c.num ? 'right' : (c.center ? 'center' : 'left'),
                                            whiteSpace: c.key === 'DS_PRODUTO' || c.key === 'MOTIVO' ? 'normal' : 'nowrap',
                                            maxWidth: c.key === 'NR_CHAVE_ACESSO' ? 220 : undefined,
                                            overflow: c.key === 'NR_CHAVE_ACESSO' ? 'hidden' : undefined,
                                            textOverflow: c.key === 'NR_CHAVE_ACESSO' ? 'ellipsis' : undefined,
                                        }}>
                                            {c.num ? fmtNum(row[c.key]) : (row[c.key] ?? '')}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Paginação */}
            {divergencias.length > ITENS_POR_PAGINA && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                    <button onClick={() => setPaginaAtual(1)} disabled={paginaAtual === 1} style={btnPaginacao(paginaAtual === 1)}>« Primeira</button>
                    <button onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))} disabled={paginaAtual === 1} style={btnPaginacao(paginaAtual === 1)}>Anterior</button>
                    <span style={{ padding: '8px 14px', background: COR, color: '#fff', borderRadius: 6, fontWeight: 700, fontSize: 13 }}>
                        {paginaAtual} / {totalPaginas}
                    </span>
                    <button onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))} disabled={paginaAtual === totalPaginas} style={btnPaginacao(paginaAtual === totalPaginas)}>Próxima</button>
                    <button onClick={() => setPaginaAtual(totalPaginas)} disabled={paginaAtual === totalPaginas} style={btnPaginacao(paginaAtual === totalPaginas)}>Última »</button>
                </div>
            )}
        </div>
    );
};

const btnPaginacao = (desabilitado) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '8px 12px',
    background: desabilitado ? '#f1f5f9' : '#fff',
    color: desabilitado ? '#94a3b8' : '#475569',
    border: '1px solid #cbd5e1', borderRadius: 6,
    fontWeight: 700, fontSize: 13,
    cursor: desabilitado ? 'not-allowed' : 'pointer',
});

export default FiscalVerificacaoAliquotas;
